<script setup>
import { ref, watch, computed } from 'vue';
import schedulingAPI from '../api/scheduling';

const props = defineProps({
  enrollmentId: {
    type: [Number, String],
    default: null,
  },
  enrollmentInfo: {
    type: Object,
    default: null,
  },
  services: {
    type: Array,
    default: () => [],
  },
  professionals: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close', 'updated']);

const actualEnrollmentId = computed(() => {
  return props.enrollmentInfo?.enrollmentId || props.enrollmentId;
});

const isLoading = ref(false);
const isActionLoading = ref(false);
const enrollmentData = ref(null);
const gridMatrix = ref({});
const activeTab = ref('attendance'); // 'attendance' | 'history' | 'edit'
const showRescheduleBox = ref(false);

const isEditingAttendanceNote = ref(false);
const editingHistoryAttendanceId = ref(null);
const historyAttendanceNotes = ref('');

const availableServices = ref([...props.services]);
const availableProfessionals = ref([...props.professionals]);

// Attendance Form State
const attendanceNotes = ref('');

// Reschedule Form State
const targetDate = ref(new Date().toISOString().split('T')[0]);
const targetDay = ref('wednesday');
const targetSlot = ref('09:00');
const rescheduleNotes = ref('');

// Renewal Sessions State
const additionalSessions = ref(10);

// Edit Form State
const editSelectedDays = ref(['monday']);
const isCustomEditSchedule = ref(false);
const editScheduleByDay = ref({
  monday: { time_slot: '08:00', professional_id: null },
  tuesday: { time_slot: '08:00', professional_id: null },
  wednesday: { time_slot: '08:00', professional_id: null },
  thursday: { time_slot: '08:00', professional_id: null },
  friday: { time_slot: '08:00', professional_id: null },
  saturday: { time_slot: '08:00', professional_id: null },
});

const toggleEditDay = dayKey => {
  if (editSelectedDays.value.includes(dayKey)) {
    if (editSelectedDays.value.length > 1) {
      editSelectedDays.value = editSelectedDays.value.filter(d => d !== dayKey);
    }
  } else {
    editSelectedDays.value.push(dayKey);
  }
};

const editForm = ref({
  scheduling_service_id: null,
  scheduling_professional_id: null,
  day_of_week: 'monday',
  time_slot: '08:00',
  total_sessions: 10,
  completed_sessions: 0,
  price: 0,
  status: 'active',
  notes: '',
});

const dayLabels = {
  monday: 'Segunda-feira',
  tuesday: 'Terça-feira',
  wednesday: 'Quarta-feira',
  thursday: 'Quinta-feira',
  friday: 'Sexta-feira',
  saturday: 'Sábado',
  sunday: 'Domingo',
};

const dayKeyMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const availableWeekDays = [
  { key: 'monday', label: 'Seg', full: 'Segunda-feira', dayNum: 1 },
  { key: 'tuesday', label: 'Ter', full: 'Terça-feira', dayNum: 2 },
  { key: 'wednesday', label: 'Qua', full: 'Quarta-feira', dayNum: 3 },
  { key: 'thursday', label: 'Qui', full: 'Quinta-feira', dayNum: 4 },
  { key: 'friday', label: 'Sex', full: 'Sexta-feira', dayNum: 5 },
  { key: 'saturday', label: 'Sáb', full: 'Sábado', dayNum: 6 },
];

const timeSlots = [
  '06:00', '07:00', '08:00', '09:00', '10:00', '11:00',
  '12:00', '13:00', '14:00', '15:00', '16:00', '17:00',
  '18:00', '19:00', '20:00', '21:00', '22:00'
];

const formattedDayOfWeek = computed(() => {
  const d = props.enrollmentInfo?.dayKey || enrollmentData.value?.day_of_week;
  return dayLabels[d?.toLowerCase()] || d || '';
});

const todayDayKey = computed(() => {
  const dayIndex = new Date().getDay();
  return dayKeyMap[dayIndex];
});

const isToday = computed(() => {
  if (props.enrollmentInfo?.isToday) return true;
  const d = props.enrollmentInfo?.dayKey || enrollmentData.value?.day_of_week;
  return d?.toLowerCase() === todayDayKey.value;
});

const hasPastPending = computed(() => {
  return Boolean(props.enrollmentInfo?.has_past_pending || props.enrollmentInfo?.past_pending_appointment_id);
});

const pastPendingDateLabel = computed(() => {
  return props.enrollmentInfo?.past_pending_date || '';
});

const pastPendingTimeLabel = computed(() => {
  return props.enrollmentInfo?.past_pending_time || '';
});

const isAlreadyAttended = computed(() => {
  if (props.enrollmentInfo?.currentWeekAttended) return true;
  const slotDate = props.enrollmentInfo?.slotDate;
  if (slotDate && enrollmentData.value?.attendances) {
    return enrollmentData.value.attendances.some(att => att.attendance_date === slotDate);
  }
  return false;
});

const latestAttendanceForSlot = computed(() => {
  const slotDate = props.enrollmentInfo?.slotDate;
  if (!enrollmentData.value?.attendances) return null;
  if (slotDate) {
    return enrollmentData.value.attendances.find(att => att.attendance_date === slotDate);
  }
  return enrollmentData.value.attendances[0];
});

const isSlotFull = (dayKey, slotTime) => {
  const dayData = gridMatrix.value[dayKey];
  if (!dayData) return false;
  const slotData = dayData[slotTime];
  if (!slotData) return false;
  return slotData.is_full || slotData.free_slots_count <= 0;
};

const selectTargetDay = dayKey => {
  targetDay.value = dayKey;
  const targetDayObj = availableWeekDays.find(d => d.key === dayKey);
  if (targetDayObj) {
    const today = new Date();
    const currentWday = today.getDay();
    let diff = targetDayObj.dayNum - currentWday;
    if (diff <= 0) diff += 7;
    const nextDate = new Date();
    nextDate.setDate(today.getDate() + diff);
    targetDate.value = nextDate.toISOString().split('T')[0];
  }
};

const onTargetDateChange = () => {
  if (!targetDate.value) return;
  const d = new Date(`${targetDate.value}T12:00:00`);
  const wday = d.getDay();
  const dayK = dayKeyMap[wday];
  if (dayK && dayK !== 'sunday') {
    targetDay.value = dayK;
  }
};

const fetchEnrollmentDetails = async () => {
  if (!actualEnrollmentId.value) return;
  isLoading.value = true;
  try {
    const [res, srvRes, profRes] = await Promise.all([
      schedulingAPI.getEnrollment(actualEnrollmentId.value),
      availableServices.value.length === 0 ? schedulingAPI.getServices() : Promise.resolve({ data: availableServices.value }),
      availableProfessionals.value.length === 0 ? schedulingAPI.getProfessionals() : Promise.resolve({ data: availableProfessionals.value }),
    ]);

    enrollmentData.value = res.data;
    availableServices.value = srvRes.data || [];
    availableProfessionals.value = profRes.data || [];

    // Preenche os dados de edição
    if (enrollmentData.value) {
      editForm.value = {
        scheduling_service_id: enrollmentData.value.service?.id || null,
        scheduling_professional_id: enrollmentData.value.professional?.id || null,
        day_of_week: enrollmentData.value.day_of_week || 'monday',
        time_slot: enrollmentData.value.time_slot || '08:00',
        total_sessions: enrollmentData.value.total_sessions || 10,
        completed_sessions: enrollmentData.value.completed_sessions || 0,
        price: enrollmentData.value.price || 0,
        status: enrollmentData.value.status || 'active',
        notes: enrollmentData.value.notes || '',
      };

      const days = enrollmentData.value.all_days_of_week || [enrollmentData.value.day_of_week || 'monday'];
      editSelectedDays.value = [...days];
      isCustomEditSchedule.value = false;

      availableWeekDays.forEach(d => {
        const dayCfg = enrollmentData.value.schedule_by_day?.[d.key];
        editScheduleByDay.value[d.key] = {
          time_slot: dayCfg?.time_slot || enrollmentData.value.time_slot || '08:00',
          professional_id: dayCfg?.professional_id || enrollmentData.value.professional?.id || null,
        };
      });
    }

    // Busca a grade de disponibilidade para detectar horários lotados
    const profId = enrollmentData.value?.professional?.id;
    const gridRes = await schedulingAPI.getFixedGrid({ professional_id: profId });
    gridMatrix.value = gridRes.data.grid || {};

    // Se já havia anotação nesta sessão/dia, carrega no campo
    if (latestAttendanceForSlot.value?.prontuario_notes) {
      attendanceNotes.value = latestAttendanceForSlot.value.prontuario_notes;
    }
  } catch (err) {
    console.error('Erro ao carregar matrícula:', err);
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => [actualEnrollmentId.value, props.enrollmentInfo],
  () => {
    attendanceNotes.value = '';
    rescheduleNotes.value = '';
    showRescheduleBox.value = false;
    isEditingAttendanceNote.value = false;
    editingHistoryAttendanceId.value = null;
    activeTab.value = 'attendance';
    fetchEnrollmentDetails();
  },
  { immediate: true }
);

const handleCheckIn = async () => {
  isActionLoading.value = true;
  try {
    const appId = props.enrollmentInfo?.past_pending_appointment_id || props.enrollmentInfo?.appointmentId;
    if (appId) {
      await schedulingAPI.updateAppointmentStatus(appId, 'completed', {
        prontuario_notes: attendanceNotes.value.trim(),
      });
    } else if (actualEnrollmentId.value) {
      const attDate = props.enrollmentInfo?.past_pending_date || props.enrollmentInfo?.slotDate || new Date().toISOString().split('T')[0];
      await schedulingAPI.checkInEnrollment(actualEnrollmentId.value, {
        prontuario_notes: attendanceNotes.value.trim(),
        attendance_date: attDate,
      });
    }
    await fetchEnrollmentDetails();
    emit('updated');
  } catch (err) {
    alert(err.response?.data?.message || 'Erro ao registrar presença.');
  } finally {
    isActionLoading.value = false;
  }
};

const handleMarkAbsence = async () => {
  const appId = props.enrollmentInfo?.past_pending_appointment_id || props.enrollmentInfo?.appointmentId;
  if (!appId) {
    alert('Nenhum atendimento específico selecionado para registrar falta.');
    return;
  }
  if (!confirm('Deseja registrar falta para este atendimento?')) return;
  isActionLoading.value = true;
  try {
    await schedulingAPI.updateAppointmentStatus(appId, 'cancelled', {
      notes: 'Falta registrada na recepção.',
    });
    await fetchEnrollmentDetails();
    emit('updated');
  } catch (err) {
    alert(err.response?.data?.message || 'Erro ao registrar falta.');
  } finally {
    isActionLoading.value = false;
  }
};

const handleUpdateAttendanceNote = async (attendanceId, notes) => {
  if (!attendanceId) return;
  isActionLoading.value = true;
  try {
    await schedulingAPI.updateAttendance(attendanceId, {
      prontuario_notes: notes.trim(),
    });
    await fetchEnrollmentDetails();
    isEditingAttendanceNote.value = false;
    editingHistoryAttendanceId.value = null;
    emit('updated');
    alert('Prontuário atualizado com sucesso!');
  } catch (err) {
    alert(err.response?.data?.message || 'Erro ao atualizar prontuário.');
  } finally {
    isActionLoading.value = false;
  }
};

const handleReschedule = async () => {
  if (!actualEnrollmentId.value) return;
  if (isSlotFull(targetDay.value, targetSlot.value)) {
    alert('Este horário está lotado para o dia selecionado. Por favor, escolha outro horário livre.');
    return;
  }

  isActionLoading.value = true;
  try {
    await schedulingAPI.rescheduleEnrollment(actualEnrollmentId.value, {
      target_date: targetDate.value,
      target_day_of_week: targetDay.value,
      target_time_slot: targetSlot.value,
      notes: rescheduleNotes.value.trim(),
    });
    rescheduleNotes.value = '';
    showRescheduleBox.value = false;
    await fetchEnrollmentDetails();
    emit('updated');
    alert(`Sessão remarcada para ${dayLabels[targetDay.value] || targetDay.value} às ${targetSlot.value}!`);
  } catch (err) {
    alert(err.response?.data?.message || 'Erro ao remarcar sessão.');
  } finally {
    isActionLoading.value = false;
  }
};

const handleRenew = async () => {
  if (!actualEnrollmentId.value) return;
  isActionLoading.value = true;
  try {
    await schedulingAPI.renewEnrollment(actualEnrollmentId.value, {
      additional_sessions: additionalSessions.value,
    });
    await fetchEnrollmentDetails();
    emit('updated');
    alert(`Pacote renovado (+${additionalSessions.value} sessões) com sucesso!`);
  } catch (err) {
    alert('Erro ao renovar pacote.');
  } finally {
    isActionLoading.value = false;
  }
};

const handleSaveEdit = async () => {
  if (!actualEnrollmentId.value) return;
  if (editSelectedDays.value.length === 0) {
    alert('Selecione pelo menos um dia da semana para a matrícula.');
    return;
  }

  isActionLoading.value = true;
  try {
    const payload = {
      ...editForm.value,
      days_of_week: editSelectedDays.value,
      schedule_by_day: isCustomEditSchedule.value ? editScheduleByDay.value : null,
    };
    await schedulingAPI.updateEnrollment(actualEnrollmentId.value, payload);
    emit('updated');
    emit('close');
  } catch (err) {
    alert(err.response?.data?.message || 'Erro ao atualizar dados da matrícula.');
  } finally {
    isActionLoading.value = false;
  }
};

const handleDelete = async () => {
  if (!actualEnrollmentId.value || !confirm('Tem certeza que deseja cancelar esta matrícula e liberar a vaga na grade?')) return;
  isActionLoading.value = true;
  try {
    await schedulingAPI.deleteEnrollment(actualEnrollmentId.value);
    emit('updated');
    emit('close');
  } catch (err) {
    alert('Erro ao remover matrícula.');
  } finally {
    isActionLoading.value = false;
  }
};

const progressWidth = computed(() => {
  if (!enrollmentData.value) return 0;
  return Math.min(enrollmentData.value.progress_percentage || 0, 100);
});
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in"
    @click.self="$emit('close')"
  >
    <div
      class="w-full max-w-xl h-[670px] max-h-[92vh] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden text-slate-800 dark:text-slate-100 flex flex-col justify-between"
    >
      <!-- Header Fixo -->
      <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-950/60 shrink-0">
        <div class="flex items-center gap-3">
          <div
            class="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-600/20 border border-blue-200 dark:border-blue-500/30 flex items-center justify-center text-blue-700 dark:text-blue-400 font-bold text-base"
          >
            {{ enrollmentData?.patient?.name?.charAt(0) || '👤' }}
          </div>
          <div>
            <h3 class="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <span>{{ enrollmentData?.patient?.name || 'Carregando...' }}</span>
              <span
                v-if="enrollmentData?.session_type === 'trial'"
                class="text-[10px] px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30 font-bold flex items-center gap-1"
              >
                <span>⭐</span> Aula Experimental
              </span>
              <span
                v-else-if="enrollmentData?.status === 'completed'"
                class="text-[10px] px-2 py-0.5 rounded bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30 font-bold"
              >
                Concluído
              </span>
              <span
                v-else
                class="text-[10px] px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30 font-bold"
              >
                Ativo
              </span>
            </h3>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{ enrollmentData?.service?.name }} • {{ enrollmentData?.professional?.name }} •
              <span class="text-blue-600 dark:text-blue-400 font-bold">
                {{ formattedDayOfWeek }} às {{ props.enrollmentInfo?.timeSlot || enrollmentData?.time_slot }}
              </span>
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <!-- Edit State Buttons -->
          <template v-if="activeTab === 'edit'">
            <button
              type="button"
              class="text-xs font-semibold px-2.5 py-1 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white cursor-pointer"
              @click="activeTab = 'attendance'"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="text-xs font-bold px-3 py-1 rounded-lg bg-blue-600 hover:bg-blue-500 text-white shadow-xs flex items-center gap-1 cursor-pointer disabled:opacity-50"
              :disabled="isActionLoading"
              @click="handleSaveEdit"
            >
              <span>✓</span>
              <span>{{ isActionLoading ? 'Salvando...' : 'Confirmar' }}</span>
            </button>
          </template>

          <!-- Attendance State Button -->
          <button
            v-else
            type="button"
            class="text-xs font-semibold px-2.5 py-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/80 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-750 flex items-center gap-1 cursor-pointer"
            title="Editar dias e horários da matrícula"
            @click="activeTab = 'edit'"
          >
            <span>✏️</span>
            <span>Editar</span>
          </button>

          <button type="button" class="text-slate-400 hover:text-slate-600 dark:hover:text-white cursor-pointer ml-1" @click="$emit('close')">✕</button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="flex-1 flex justify-center items-center">
        <div class="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
      </div>

      <!-- Content Area Principal -->
      <div v-else-if="enrollmentData" class="p-6 flex-1 flex flex-col overflow-hidden space-y-3.5">
        <!-- Progress Bar / Info Card Fixo -->
        <div class="p-4 rounded-2xl bg-slate-50 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 space-y-2 shrink-0">
          <template v-if="enrollmentData.session_type === 'trial'">
            <div class="flex items-center justify-between text-xs">
              <span class="font-bold text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                <span>⭐</span>
                <span>Modalidade: Aula Experimental</span>
              </span>
              <span class="font-bold text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-500/20 border border-amber-200 dark:border-amber-500/30 px-2 py-0.5 rounded-md text-[10px]">
                Gratuito (Cortesia / R$ 0,00)
              </span>
            </div>
            <div class="text-[11px] text-slate-500 dark:text-slate-400 flex items-center justify-between pt-1 border-t border-slate-200 dark:border-slate-800/60">
              <span>Data da Aula: <strong class="text-slate-800 dark:text-slate-200">{{ enrollmentData.start_date }}</strong></span>
              <span class="text-slate-600 dark:text-slate-300 font-medium">1 sessão única para fins de análise e conversão</span>
            </div>
          </template>

          <template v-else>
            <div class="flex items-center justify-between text-xs">
              <span class="font-bold text-slate-700 dark:text-slate-300">Progresso do Pacote</span>
              <span class="font-mono font-extrabold text-blue-600 dark:text-blue-400">
                {{ enrollmentData.completed_sessions }} / {{ enrollmentData.total_sessions }} sessões ({{ enrollmentData.progress_percentage }}%)
              </span>
            </div>

            <!-- Progress Track -->
            <div class="w-full h-2.5 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full transition-all duration-500"
                :class="enrollmentData.completed_sessions >= enrollmentData.total_sessions ? 'bg-emerald-500' : 'bg-blue-600'"
                :style="{ width: `${progressWidth}%` }"
              />
            </div>

            <div class="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
              <span>Início: {{ enrollmentData.start_date }}</span>
              <span v-if="enrollmentData.remaining_sessions > 0" class="text-slate-600 dark:text-slate-300">
                Faltam <strong>{{ enrollmentData.remaining_sessions }} sessões</strong>
              </span>
              <span v-else class="text-emerald-600 dark:text-emerald-400 font-bold">
                🎉 Pacote finalizado (Vaga liberada)
              </span>
            </div>
          </template>
        </div>

        <!-- Navigation Tabs Fixo -->
        <div class="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2 shrink-0">
          <button
            type="button"
            class="px-3 py-1.5 text-xs font-bold rounded-xl transition-all cursor-pointer"
            :class="activeTab === 'attendance' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'"
            @click="activeTab = 'attendance'"
          >
            📝 Atendimento & Presença
          </button>
          <button
            type="button"
            class="px-3 py-1.5 text-xs font-bold rounded-xl transition-all cursor-pointer"
            :class="activeTab === 'history' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'"
            @click="activeTab = 'history'"
          >
            📜 Histórico de Prontuário ({{ enrollmentData.attendances?.length || 0 }})
          </button>
        </div>

        <!-- Caixa Secundária Dinâmica -->
        <div class="flex-1 overflow-y-auto space-y-3 pr-1">
          <!-- TAB 1: Atendimento & Ações -->
          <div v-if="activeTab === 'attendance'" class="space-y-3 animate-fade-in">
            <div class="p-4 rounded-2xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800 space-y-3">
              <!-- Informação do Dia do Atendimento com Status -->
              <div class="flex items-center justify-between pb-2 border-b border-slate-200 dark:border-slate-800/80">
                <span class="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                  <span class="text-blue-600 dark:text-blue-400">📅</span>
                  <span>
                    Atendimento:
                    <strong>
                      {{ formattedDayOfWeek }}
                      às {{ props.enrollmentInfo?.timeSlot || enrollmentData.time_slot }}
                    </strong>
                  </span>
                </span>

                <!-- Badge de Presença ou Indicador de Hoje/Sessão -->
                <span
                  v-if="isAlreadyAttended"
                  class="text-[11px] font-bold px-2.5 py-0.5 rounded-lg bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/40 flex items-center gap-1"
                >
                  <span>✓</span> Presente
                </span>
                <span
                  v-else-if="props.enrollmentInfo?.isToday"
                  class="text-[11px] font-bold px-2.5 py-0.5 rounded-lg bg-blue-50 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/40 flex items-center gap-1"
                >
                  <span>⚡</span> Aula de Hoje
                </span>
                <span
                  v-else
                  class="text-[11px] font-mono font-semibold px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700"
                >
                  Sessão {{ Math.min(enrollmentData.completed_sessions + 1, enrollmentData.total_sessions) }} de {{ enrollmentData.total_sessions }}
                </span>
              </div>

              <!-- CENÁRIO 1: ATENDIMENTO JÁ FOI FINALIZADO / CONFIRMADO -->
              <div v-if="isAlreadyAttended" class="space-y-3 pt-1">
                <!-- Modo Edição de Prontuário da Sessão Concluída -->
                <div v-if="isEditingAttendanceNote" class="space-y-2">
                  <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300">
                    Editar Evolução / Prontuário desta Sessão:
                  </label>
                  <textarea
                    v-model="attendanceNotes"
                    rows="3"
                    placeholder="Atualize as anotações do prontuário..."
                    class="w-full p-2.5 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl focus:ring-1 focus:ring-blue-500 focus:outline-none text-slate-900 dark:text-white resize-none"
                  />
                  <div class="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      class="px-3 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-slate-900 rounded-xl cursor-pointer"
                      @click="isEditingAttendanceNote = false"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      class="px-4 py-1.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-500 rounded-xl shadow-md cursor-pointer disabled:opacity-50"
                      :disabled="isActionLoading"
                      @click="handleUpdateAttendanceNote(latestAttendanceForSlot?.id, attendanceNotes)"
                    >
                      {{ isActionLoading ? 'Salvando...' : 'Salvar Alteração no Prontuário' }}
                    </button>
                  </div>
                </div>

                <!-- Modo Leitura / Visualização Fechada -->
                <div v-else class="space-y-2.5">
                  <div class="p-3.5 bg-white dark:bg-slate-900/90 rounded-xl border border-slate-200 dark:border-slate-800 space-y-1.5">
                    <div class="flex items-center justify-between">
                      <span class="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider">
                        Evolução / Prontuário Registrado
                      </span>
                      <button
                        type="button"
                        class="text-[11px] text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-bold flex items-center gap-1 cursor-pointer bg-blue-50 dark:bg-blue-500/10 px-2 py-0.5 rounded-lg border border-blue-200 dark:border-blue-500/20"
                        @click="isEditingAttendanceNote = true; attendanceNotes = latestAttendanceForSlot?.prontuario_notes || ''"
                      >
                        <span>✏️</span>
                        <span>Editar Prontuário</span>
                      </button>
                    </div>

                    <p v-if="latestAttendanceForSlot?.prontuario_notes" class="text-slate-800 dark:text-slate-200 text-xs leading-relaxed whitespace-pre-wrap">
                      {{ latestAttendanceForSlot.prontuario_notes }}
                    </p>
                    <p v-else class="text-slate-500 dark:text-slate-400 text-xs italic">
                      Presença confirmada sem anotações clínicas adicionais.
                    </p>
                  </div>

                  <div class="px-3 py-1.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-500/40 text-emerald-800 dark:text-emerald-300 text-[11px] font-medium flex items-center gap-2">
                    <span>✓</span>
                    <span>Atendimento concluído. Para alterar anotações, use o botão "Editar Prontuário".</span>
                  </div>
                </div>
              </div>

              <!-- CENÁRIO 2: AINDA NÃO CONFIRMOU PRESENÇA (EM ABERTO) -->
              <div v-else class="space-y-3">
                <!-- BANNER DE ALERTA SE HOUVER SESSÃO PASSADA PENDENTE -->
                <div
                  v-if="hasPastPending"
                  class="p-3.5 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-500/50 flex items-start gap-3 animate-fade-in"
                >
                  <span class="text-xl leading-none">⚠️</span>
                  <div class="flex-1 text-xs">
                    <div class="font-bold text-amber-800 dark:text-amber-300">
                      Atendimento anterior pendente de confirmação
                    </div>
                    <div class="text-[11px] text-amber-900/80 dark:text-amber-200/80 mt-0.5 leading-relaxed">
                      Existe uma sessão anterior <span v-if="pastPendingDateLabel" class="font-bold text-slate-900 dark:text-white">({{ pastPendingDateLabel }} às {{ pastPendingTimeLabel || props.enrollmentInfo?.timeSlot || '08:00' }})</span> aguardando confirmação de presença ou registro de falta.
                    </div>
                  </div>
                </div>

                <!-- Campo de Evolução Aberto para a Sessão -->
                <div>
                  <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Evolução / Anotações do Prontuário nesta Sessão
                  </label>
                  <textarea
                    v-model="attendanceNotes"
                    rows="2"
                    placeholder="Procedimentos realizados, queixas apresentadas, orientações passadas ao paciente..."
                    class="w-full p-2.5 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl focus:ring-1 focus:ring-blue-500 focus:outline-none text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 resize-none"
                  />
                </div>

                <div class="flex items-center justify-between pt-1 gap-2 flex-wrap">
                  <!-- Botão de Remarcar -->
                  <button
                    type="button"
                    class="px-3.5 py-2 text-xs font-bold rounded-xl border transition-all cursor-pointer flex items-center gap-1.5"
                    :class="
                      showRescheduleBox
                        ? 'bg-indigo-600 border-indigo-500 text-white shadow-xs'
                        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-indigo-600 dark:text-indigo-400 hover:border-indigo-400 hover:text-indigo-800 dark:hover:text-white'
                    "
                    @click="showRescheduleBox = !showRescheduleBox"
                  >
                    <span>🔄</span>
                    <span>{{ showRescheduleBox ? 'Fechar Remarcação' : 'Remarcar' }}</span>
                  </button>

                  <div class="flex items-center gap-2">
                    <!-- Botão de Registrar Falta -->
                    <button
                      v-if="hasPastPending"
                      type="button"
                      class="px-3 py-2 text-xs font-bold text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-white bg-rose-50 dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/60 border border-rose-200 dark:border-rose-500/40 rounded-xl transition-all cursor-pointer flex items-center gap-1"
                      :disabled="isActionLoading"
                      title="Registrar ausência/falta do paciente"
                      @click="handleMarkAbsence"
                    >
                      <span>❌</span>
                      <span>Registrar Falta</span>
                    </button>

                    <!-- Botão de Presença Retroativa -->
                    <button
                      v-if="hasPastPending"
                      type="button"
                      class="px-4 py-2 text-xs font-bold text-amber-800 dark:text-amber-300 hover:text-white bg-amber-100 dark:bg-amber-950/80 hover:bg-amber-600 border border-amber-300 dark:border-amber-500/60 hover:border-amber-500 rounded-xl shadow-xs cursor-pointer disabled:opacity-50 flex items-center gap-1.5 transition-all"
                      :disabled="isActionLoading || enrollmentData.completed_sessions >= enrollmentData.total_sessions"
                      @click="handleCheckIn"
                    >
                      <span class="text-amber-600 dark:text-amber-400 group-hover:text-white">✓</span>
                      <span>{{ isActionLoading ? 'Registrando...' : 'Confirmar Presença Retroativa (+1)' }}</span>
                    </button>

                    <!-- Botão de Presença Hoje -->
                    <button
                      v-else-if="isToday"
                      type="button"
                      class="px-5 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 rounded-xl shadow-md cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
                      :disabled="isActionLoading || enrollmentData.completed_sessions >= enrollmentData.total_sessions"
                      @click="handleCheckIn"
                    >
                      <span>✓</span>
                      <span>{{ isActionLoading ? 'Registrando...' : 'Confirmar Presença Hoje (+1)' }}</span>
                    </button>

                    <!-- Trava Futura -->
                    <div
                      v-else
                      class="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 text-xs font-semibold flex items-center gap-1.5 cursor-not-allowed select-none"
                      title="A presença só pode ser confirmada no dia da aula ou retroativamente quando pendente"
                    >
                      <span>🔒</span>
                      <span>Presença liberada no dia</span>
                    </div>
                  </div>
                </div>

                <!-- PAINEL INLINE DE REMARCAÇÃO / REPOSIÇÃO -->
                <div
                  v-if="showRescheduleBox"
                  class="p-3.5 rounded-xl bg-white dark:bg-slate-900 border border-indigo-200 dark:border-indigo-500/40 space-y-3 animate-fade-in"
                >
                  <div class="flex items-center justify-between">
                    <span class="text-xs font-bold text-indigo-700 dark:text-indigo-300 flex items-center gap-1.5">
                      <span>🔄</span> Escolha o dia e horário para Reposição
                    </span>
                    <span class="text-[10px] text-slate-500 dark:text-slate-400">Encaixe inteligente</span>
                  </div>

                  <!-- Day of Week for Reschedule -->
                  <div>
                    <label class="block text-[10px] text-slate-600 dark:text-slate-400 mb-1">Dia da Semana:</label>
                    <div class="grid grid-cols-6 gap-1">
                      <button
                        v-for="d in availableWeekDays"
                        :key="d.key"
                        type="button"
                        class="py-1 text-xs font-bold rounded-lg border transition-all cursor-pointer text-center"
                        :class="targetDay === d.key ? 'bg-indigo-600 border-indigo-500 text-white shadow-xs' : 'bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'"
                        @click="selectTargetDay(d.key)"
                      >
                        {{ d.label }}
                      </button>
                    </div>
                  </div>

                  <!-- Date Selection -->
                  <div>
                    <label class="block text-[10px] text-slate-600 dark:text-slate-400 mb-1">Data da Reposição:</label>
                    <input
                      v-model="targetDate"
                      type="date"
                      class="w-full h-8 px-2.5 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white font-mono cursor-pointer focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      @change="onTargetDateChange"
                    />
                  </div>

                  <!-- Clean Visual Time Slot Selector -->
                  <div>
                    <div class="flex items-center justify-between mb-1.5">
                      <label class="text-[10px] text-slate-600 dark:text-slate-400">Horário de Destino:</label>
                      <span class="text-[10px] text-slate-500 flex items-center gap-2">
                        <span class="flex items-center gap-1">
                          <span class="w-2 h-2 rounded-full border border-rose-400 dark:border-red-500/60 bg-rose-100 dark:bg-red-950/40" />
                          <span class="text-rose-600 dark:text-red-400">Lotado</span>
                        </span>
                        <span class="flex items-center gap-1">
                          <span class="w-2 h-2 rounded-full border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800" />
                          <span class="text-slate-700 dark:text-slate-300">Livre</span>
                        </span>
                      </span>
                    </div>

                    <div class="grid grid-cols-6 gap-1.5 max-h-32 overflow-y-auto p-1 bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800 rounded-xl">
                      <button
                        v-for="s in timeSlots"
                        :key="s"
                        type="button"
                        class="py-1 text-[11px] font-bold rounded-lg border text-center transition-all"
                        :class="[
                          isSlotFull(targetDay, s)
                            ? 'border-rose-300 dark:border-red-500/60 bg-rose-50 dark:red-950/25 text-rose-600 dark:text-red-400 opacity-60 cursor-not-allowed'
                            : (targetSlot === s
                              ? 'bg-indigo-600 border-indigo-500 text-white shadow-xs cursor-pointer'
                              : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-indigo-400 hover:text-indigo-700 dark:hover:text-white cursor-pointer')
                        ]"
                        :disabled="isSlotFull(targetDay, s)"
                        :title="isSlotFull(targetDay, s) ? 'Horário lotado / sem vagas' : `Horário livre (${s})`"
                        @click="targetSlot = s"
                      >
                        {{ s }}
                      </button>
                    </div>
                  </div>

                  <div>
                    <input
                      v-model="rescheduleNotes"
                      type="text"
                      placeholder="Motivo da reposição (opcional)..."
                      class="w-full h-8 px-2.5 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>

                  <div class="flex justify-end">
                    <button
                      type="button"
                      class="px-4 py-1.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg shadow-xs cursor-pointer disabled:opacity-50"
                      :disabled="isActionLoading || isSlotFull(targetDay, targetSlot)"
                      @click="handleReschedule"
                    >
                      {{ isActionLoading ? 'Salvando...' : 'Confirmar Encaixe de Reposição' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Quick Package Renewal Card -->
            <div class="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-800/80 flex items-center justify-between">
              <div>
                <span class="text-xs font-bold text-slate-900 dark:text-slate-200 block">Renovação de Pacote</span>
                <span class="text-[10px] text-slate-500 dark:text-slate-400">Mantém a vaga fixa e adiciona mais sessões</span>
              </div>

              <div class="flex items-center gap-2">
                <input
                  v-model.number="additionalSessions"
                  type="number"
                  min="1"
                  max="50"
                  class="w-16 h-8 px-2 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white font-bold text-center focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                <button
                  type="button"
                  class="px-3 py-1.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-500 rounded-xl shadow-xs cursor-pointer disabled:opacity-50"
                  :disabled="isActionLoading"
                  @click="handleRenew"
                >
                  🔁 Renovar
                </button>
              </div>
            </div>
          </div>

          <!-- TAB 2: Histórico de Prontuário -->
          <div v-if="activeTab === 'history'" class="space-y-2.5 animate-fade-in">
            <div v-if="!enrollmentData.attendances || enrollmentData.attendances.length === 0" class="p-12 text-center text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-800/80 rounded-2xl">
              Nenhuma presença registrada ainda para este pacote.
            </div>
            <div
              v-for="att in enrollmentData.attendances"
              :key="att.id"
              class="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 text-xs space-y-2 shadow-xs"
            >
              <div class="flex items-center justify-between">
                <span class="font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                  <span class="text-emerald-600 dark:text-emerald-400 font-bold">✓</span> Sessão {{ att.session_number }} de {{ enrollmentData.total_sessions }}
                </span>
                <div class="flex items-center gap-2">
                  <span class="text-slate-500 dark:text-slate-400 font-mono text-[11px] flex items-center gap-1">
                    <span>📅</span> {{ att.attendance_date }} às {{ att.time_slot }}
                  </span>
                  <button
                    v-if="editingHistoryAttendanceId !== att.id"
                    type="button"
                    class="text-[11px] text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-bold px-2 py-0.5 bg-blue-50 dark:bg-blue-500/10 rounded-lg border border-blue-200 dark:border-blue-500/20 cursor-pointer"
                    title="Editar anotação desta sessão"
                    @click="editingHistoryAttendanceId = att.id; historyAttendanceNotes = att.prontuario_notes || ''"
                  >
                    ✏️ Editar
                  </button>
                </div>
              </div>

              <div v-if="att.day_of_week || att.professional_name" class="flex items-center gap-2 text-[10px] text-slate-500 dark:text-slate-400">
                <span v-if="att.day_of_week" class="capitalize">{{ dayLabels[att.day_of_week] || att.day_of_week }}</span>
                <span v-if="att.professional_name">• Prof: {{ att.professional_name }}</span>
                <span v-if="att.service_name">• {{ att.service_name }}</span>
              </div>

              <!-- Edição individual daquela sessão no histórico -->
              <div v-if="editingHistoryAttendanceId === att.id" class="space-y-2 pt-1">
                <textarea
                  v-model="historyAttendanceNotes"
                  rows="3"
                  class="w-full p-2.5 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl focus:ring-1 focus:ring-blue-500 focus:outline-none text-slate-900 dark:text-white resize-none"
                  placeholder="Editar anotações do prontuário desta sessão..."
                />
                <div class="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    class="px-2.5 py-1 text-xs font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-slate-900 rounded-lg cursor-pointer"
                    @click="editingHistoryAttendanceId = null"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    class="px-3.5 py-1 text-xs font-bold text-white bg-blue-600 hover:bg-blue-500 rounded-lg shadow-xs cursor-pointer disabled:opacity-50"
                    :disabled="isActionLoading"
                    @click="handleUpdateAttendanceNote(att.id, historyAttendanceNotes)"
                  >
                    {{ isActionLoading ? 'Salvando...' : 'Salvar Alteração' }}
                  </button>
                </div>
              </div>

              <!-- Visualização normal do texto da sessão -->
              <div v-else>
                <p v-if="att.prontuario_notes" class="text-slate-800 dark:text-slate-200 text-[11px] bg-white dark:bg-slate-900/80 p-2.5 rounded-xl leading-relaxed border border-slate-200 dark:border-slate-800 whitespace-pre-wrap">
                  {{ att.prontuario_notes }}
                </p>
                <p v-else class="text-slate-500 dark:text-slate-400 text-[10px] italic">
                  Presença confirmada sem anotações adicionais.
                </p>
              </div>
            </div>
          </div>

          <!-- TAB 3: EDITAR MATRÍCULA / DADOS DO AGENDAMENTO -->
          <div v-if="activeTab === 'edit'" class="space-y-3.5 animate-fade-in p-4 rounded-2xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800">
            <div class="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-2">
              <h4 class="text-xs font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                <span>✏️</span> Editar Dados da Matrícula
              </h4>
              <span class="text-[10px] text-slate-500 dark:text-slate-400">Altera as configurações desta vaga fixa</span>
            </div>

            <!-- Service & Professional -->
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">Serviço</label>
                <select
                  v-model="editForm.scheduling_service_id"
                  class="w-full h-8 px-2.5 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option v-for="s in availableServices" :key="s.id" :value="s.id">
                    {{ s.name }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">Profissional</label>
                <select
                  v-model="editForm.scheduling_professional_id"
                  class="w-full h-8 px-2.5 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option v-for="p in availableProfessionals" :key="p.id" :value="p.id">
                    {{ p.name }} ({{ p.specialty || 'Geral' }})
                  </option>
                </select>
              </div>
            </div>

            <!-- Week Days Selector (Seg, Ter, Qua, Qui, Sex, Sáb) -->
            <div>
              <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                Dias Fixos da Semana <span class="text-rose-500 dark:text-red-400">*</span>
              </label>
              <div class="grid grid-cols-6 gap-1.5">
                <button
                  v-for="d in availableWeekDays"
                  :key="d.key"
                  type="button"
                  class="py-2 text-xs font-bold rounded-xl border transition-all cursor-pointer text-center"
                  :class="
                    editSelectedDays.includes(d.key)
                      ? 'bg-blue-600 border-blue-500 text-white shadow-xs'
                      : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600 hover:text-slate-900 dark:hover:text-slate-200'
                  "
                  @click="toggleEditDay(d.key)"
                >
                  {{ d.label }}
                </button>
              </div>
            </div>

            <!-- Toggle: Horários Flexíveis por Dia -->
            <div class="flex items-center justify-between p-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl">
              <label class="flex items-center gap-2 cursor-pointer select-none">
                <input
                  v-model="isCustomEditSchedule"
                  type="checkbox"
                  class="w-4 h-4 rounded text-blue-600 bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-700 focus:ring-blue-500 cursor-pointer"
                />
                <span class="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Definir horários ou profissionais diferentes por dia
                </span>
              </label>
              <span class="text-[10px] text-slate-500 dark:text-slate-400">
                {{ isCustomEditSchedule ? 'Modo Flexível Ativo' : 'Horário Padrão' }}
              </span>
            </div>

            <!-- Custom Schedule by Day -->
            <div v-if="isCustomEditSchedule" class="space-y-2 animate-fade-in">
              <div
                v-for="dayKey in editSelectedDays"
                :key="dayKey"
                class="p-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl space-y-1.5"
              >
                <div class="flex items-center justify-between">
                  <span class="text-xs font-bold text-blue-600 dark:text-blue-400 capitalize">
                    🗓️ {{ availableWeekDays.find(w => w.key === dayKey)?.full || dayKey }}
                  </span>
                </div>
                <div class="grid grid-cols-2 gap-2">
                  <div>
                    <label class="block text-[10px] text-slate-500 dark:text-slate-400 mb-0.5">Horário</label>
                    <input
                      v-model="editScheduleByDay[dayKey].time_slot"
                      type="text"
                      placeholder="08:00"
                      class="w-full h-8 px-2.5 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white font-mono text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label class="block text-[10px] text-slate-500 dark:text-slate-400 mb-0.5">Profissional</label>
                    <select
                      v-model="editScheduleByDay[dayKey].professional_id"
                      class="w-full h-8 px-2 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option v-for="prof in availableProfessionals" :key="prof.id" :value="prof.id">
                        {{ prof.name }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Standard Fixed Time Slot Selection -->
            <div v-else>
              <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">Horário Fixo</label>
              <select
                v-model="editForm.time_slot"
                class="w-full h-8 px-2.5 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-mono cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option v-for="s in timeSlots" :key="s" :value="s">
                  {{ s }}
                </option>
              </select>
            </div>

            <!-- Sessions & Price -->
            <div class="grid grid-cols-3 gap-3">
              <div>
                <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">Total de Sessões</label>
                <input
                  v-model.number="editForm.total_sessions"
                  type="number"
                  min="1"
                  class="w-full h-8 px-2.5 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-bold focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">Sessões Realizadas</label>
                <input
                  v-model.number="editForm.completed_sessions"
                  type="number"
                  min="0"
                  class="w-full h-8 px-2.5 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-bold focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">Valor (R$)</label>
                <input
                  v-model.number="editForm.price"
                  type="number"
                  step="0.01"
                  min="0"
                  class="w-full h-8 px-2.5 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            <!-- Status & Notes -->
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">Status da Matrícula</label>
                <select
                  v-model="editForm.status"
                  class="w-full h-8 px-2.5 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="active">Ativo (Ocupando vaga)</option>
                  <option value="completed">Concluído (Pacote finalizado)</option>
                  <option value="cancelled">Cancelado (Vaga liberada)</option>
                </select>
              </div>

              <div>
                <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">Observações</label>
                <input
                  v-model="editForm.notes"
                  type="text"
                  placeholder="Obs internas sobre a matrícula..."
                  class="w-full h-8 px-2.5 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            <div class="flex items-center justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-800">
              <button
                type="button"
                class="px-3.5 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-slate-900 rounded-xl cursor-pointer"
                @click="activeTab = 'attendance'"
              >
                Cancelar
              </button>
              <button
                type="button"
                class="px-4 py-1.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-500 rounded-xl shadow-md cursor-pointer disabled:opacity-50"
                :disabled="isActionLoading"
                @click="handleSaveEdit"
              >
                {{ isActionLoading ? 'Salvando...' : 'Salvar Alterações da Matrícula' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Actions Fixo -->
      <div class="px-6 py-3.5 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 flex items-center justify-between shrink-0">
        <button
          type="button"
          class="text-xs font-medium text-rose-600 hover:text-rose-700 dark:text-red-400 dark:hover:text-red-300 cursor-pointer"
          :disabled="isActionLoading"
          @click="handleDelete"
        >
          Cancelar Matrícula e Liberar Vaga
        </button>

        <div class="flex items-center gap-2">
          <template v-if="activeTab === 'edit'">
            <button
              type="button"
              class="px-4 py-2 text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white cursor-pointer"
              @click="activeTab = 'attendance'"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="px-5 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-500 rounded-xl shadow-md cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
              :disabled="isActionLoading"
              @click="handleSaveEdit"
            >
              <span>✓</span>
              <span>{{ isActionLoading ? 'Salvando...' : 'Confirmar e Salvar' }}</span>
            </button>
          </template>
          <button
            v-else
            type="button"
            class="px-4 py-2 text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white cursor-pointer"
            @click="$emit('close')"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
